import { useLocalization } from '@ais-ui/locale-management';
import { PiClock } from 'react-icons/pi';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { useTimeSince } from '../../conversions/useTimeSince';
import { NoPredictionReason } from './nextPortNoPrediction';

export interface NextPortNoPredictionContext {
  /** UNLOCODE of the port the vessel is currently in, when known. */
  locode?: string;
  /** Resolved port name, when the port could be looked up. */
  portName?: string;
  /** Country name derived from the locode, when known. */
  country?: string | null;
  /** Arrival time at the current port. */
  ata?: Date | null;
}

interface NextPortNoPredictionCardProps {
  /** The backend reason, already classified for display. */
  reason: NoPredictionReason;
  /** Raw backend reason text, shown verbatim for unrecognised reasons. */
  rawReason: string;
  /** Extra context rows; only the in-port reason has any today. */
  context?: NextPortNoPredictionContext;
}

interface DetailRow {
  key: string;
  label: string;
  value: string;
}

/**
 * In-port reason can describe *where* the vessel is stuck. Everything else has
 * no supporting detail beyond the reason text itself.
 */
const buildInPortRows = (
  context: NextPortNoPredictionContext | undefined,
  formatDateTime: (date: Date | null | undefined) => string,
  timeInPort: string | null,
): DetailRow[] => {
  const rows: DetailRow[] = [];
  if (context?.locode || context?.portName) {
    const portLabel = context.portName ?? context.locode;
    rows.push({
      key: 'current-port',
      label: 'shipDetailsNextPortCurrentPort',
      value: context.country
        ? `${portLabel} · ${context.country}`
        : `${portLabel}`,
    });
  }
  if (context?.ata) {
    rows.push({
      key: 'arrived',
      label: 'shipDetailsNextPortArrived',
      value: formatDateTime(context.ata),
    });
  }
  if (context?.ata && timeInPort) {
    rows.push({
      key: 'time-in-port',
      label: 'shipDetailsNextPortTimeInPort',
      value: timeInPort,
    });
  }
  return rows;
};

/**
 * Informational card shown in the Next Port tab when the backend cannot
 * produce a prediction. Replaces the generic empty-list placeholder with the
 * reason the prediction is unavailable — see CLASSIC-10293 / CLASSIC-10320.
 */
export const NextPortNoPredictionCard = ({
  reason,
  rawReason,
  context,
}: NextPortNoPredictionCardProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone: formatDateTime } = useDateTimeFormat();
  // `ata` is in the past, so this is elapsed time (e.g. "1 day 18 hours ago").
  const timeInPort = useTimeSince(context?.ata);

  const Icon = reason.icon;
  const title =
    messages[reason.titleKey] ?? messages.shipDetailsNextPortReasonNoDataTitle;
  const body =
    reason.kind === 'unknown'
      ? rawReason
      : (messages[reason.bodyKey] ?? rawReason);
  const footer =
    messages[reason.footerKey] ??
    messages.shipDetailsNextPortReasonNoDataFooter;

  const rows =
    reason.kind === 'in-port'
      ? buildInPortRows(context, formatDateTime, timeInPort)
      : [];

  return (
    <div className="rounded-lg border bg-muted/40 p-4">
      <div className="flex items-start gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {messages.shipDetailsNextPortNoPredictionLabel ?? 'No prediction'}
          </p>
          <p className="mt-0.5 text-base font-semibold">{title}</p>
          <p className="mt-0.5 text-sm text-muted-foreground [text-wrap:pretty]">
            {body}
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <dl className="mt-3 space-y-2 border-t pt-3 text-sm">
          {rows.map((row) => (
            <div
              key={row.key}
              className="flex items-baseline justify-between gap-4"
            >
              <dt className="shrink-0 text-muted-foreground">
                {messages[row.label] ?? row.label}
              </dt>
              <dd className="min-w-0 truncate text-right font-medium tabular-nums">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-3 flex items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
        <PiClock className="size-4 shrink-0" />
        <span>{footer}</span>
      </div>
    </div>
  );
};
